import { sendGTMEvent } from '@next/third-parties/google';
import { Cart } from 'shared/types/cart';
import { Product, Variant } from 'shared/types/product';

const sendCartEvent = (
  eventName: string,
  product: Pick<Product, 'name' | 'productId'>,
  variant: Variant,
  quantity: number,
) => {
  const eventData = {
    event: eventName,
    ecommerce: {
      currency: 'EUR',
      value: ((variant?.price?.centAmount || 0) * (quantity || 1), 0) / 100,
      items: [
        {
          item_id: variant.sku, // ID unico del prodotto
          item_name: product.name || product.productId || '', // Nome del prodotto
          item_category: variant.attributes?.gender, // Genere del prodotto
          item_category2: variant.attributes?.typology, // Tipo prodotto
          item_category3: variant.attributes?.season, // Stagione
          item_brand: variant.attributes?.brand, // Marca
          item_variant: `${variant?.attributes?.colourGroupCode} / ${variant?.attributes?.size}`, // Numero della calzatura
          price: variant?.price?.centAmount && variant.price?.centAmount / 100, // Prezzo del prodotto
          quantity, // Quantità aggiunta
        },
      ],
    },
  };
  sendGTMEvent(eventData);
};

export const sendAddToCartEvent = sendCartEvent.bind(null, 'add_to_cart');

export const sendRemoveFromCartEvent = sendCartEvent.bind(null, 'remove_from_cart');

export const sendBeginCheckoutEvent = (cart: Cart) => {
  const eventData = {
    event: 'begin_checkout',
    ecommerce: {
      currency: 'EUR',
      value: cart.sum, // cart total
      coupon: cart.discountCodes?.map((discountCode) => discountCode.code).join(','),
      items: cart.lineItems?.map((lineItem) => ({
        item_id: lineItem.productId, // ID unico del prodotto
        item_name: lineItem.name, // Nome del prodotto
        item_category: lineItem.variant?.attributes?.gender, // Genere del prodotto
        item_category2: lineItem.variant?.attributes?.typology, // Tipo prodotto
        item_category3: lineItem.variant?.attributes?.season, // Stagione
        item_brand: lineItem.variant?.attributes?.brand, // Marca
        item_variant: `${lineItem.variant?.attributes?.colourGroupCode} / ${lineItem.variant?.attributes?.size}`, // Numero della calzatura
        price: lineItem.totalPrice?.centAmount && lineItem.totalPrice?.centAmount / 100, // Prezzo del prodotto
        quantity: lineItem.count, // Quantità nel carrello
      })),
    },
  };
  sendGTMEvent(eventData);
};

export const sendPurchaseEvent = (cart: Cart) => {
  const eventData = {
    event: 'purchase',
    ecommerce: {
      currency: 'EUR',
      value: cart.sum, // cart total
      coupon: cart.discountCodes?.map((discountCode) => discountCode.code).join(','),
      items: cart.lineItems?.map((lineItem) => ({
        item_id: lineItem.productId, // ID unico del prodotto
        item_name: lineItem.name, // Nome del prodotto
        item_category: lineItem.variant?.attributes?.gender, // Genere del prodotto
        item_category2: lineItem.variant?.attributes?.typology, // Tipo prodotto
        item_category3: lineItem.variant?.attributes?.season, // Stagione
        item_brand: lineItem.variant?.attributes?.brand, // Marca
        item_variant: `${lineItem.variant?.attributes?.colourGroupCode} / ${lineItem.variant?.attributes?.size}`, // Numero della calzatura
        price: lineItem.totalPrice?.centAmount && lineItem.totalPrice?.centAmount / 100, // Prezzo del prodotto
        quantity: lineItem.count, // Quantità nel carrello
      })),
    },
  };
  sendGTMEvent(eventData);
};

export const sendAddToWishlistEvent = (productId?: string, productName?: string, variant?: Variant) => {
  const eventData = {
    event: 'add_to_wishlist',
    ecommerce: {
      currency: 'EUR',
      value: (variant?.price?.centAmount || 0) / 100,
      user_id: '', // ID dell'utente (opzionale)
      items: [
        {
          item_id: productId, // ID unico del prodotto
          item_name: productName, // Nome del prodotto
          item_category: variant?.attributes?.gender, // Genere del prodotto
          item_category2: variant?.attributes?.typology, // Tipo prodotto
          item_category3: variant?.attributes?.season, // Stagione
          item_brand: variant?.attributes?.brand, // Marca
          item_variant: `${variant?.attributes?.colourGroupCode} / ${variant?.attributes?.size}`, // Numero della calzatura
          price: variant?.price?.centAmount && variant.price?.centAmount / 100, // Prezzo del prodotto
          quantity: 1, // Quantità aggiunta
        },
      ],
    },
  };
  sendGTMEvent(eventData);
};

// TODO: fired multiple times
export const sendViewCartEvent = (cart: Cart) => {
  const eventData = {
    event: 'view_cart',
    ecommerce: {
      currency: 'EUR',
      value: cart.sum, // cart total
      items: cart.lineItems?.map((lineItem) => ({
        item_id: lineItem.productId, // ID unico del prodotto
        item_name: lineItem.name, // Nome del prodotto
        item_category: lineItem.variant?.attributes?.gender, // Genere del prodotto
        item_category2: lineItem.variant?.attributes?.typology, // Tipo prodotto
        item_category3: lineItem.variant?.attributes?.season, // Stagione
        item_brand: lineItem.variant?.attributes?.brand, // Marca
        item_variant: `${lineItem.variant?.attributes?.colourGroupCode} / ${lineItem.variant?.attributes?.size}`, // Numero della calzatura
        price: lineItem.totalPrice?.centAmount && lineItem.totalPrice?.centAmount / 100, // Prezzo del prodotto
        quantity: lineItem.count, // Quantità nel carrello
      })),
    },
  };
  sendGTMEvent(eventData);
};

export const sendViewItemEvent = (product: Product, variant: Variant) => {
  const eventData = {
    event: 'view_item',
    ecommerce: {
      currency: 'EUR',
      value: variant.price?.centAmount && variant.price?.centAmount / 100, // Prezzo
      items: [
        {
          item_id: product.productId, // ID unico del prodotto
          item_name: product.name, // Nome del prodotto
          item_category: variant?.attributes?.gender, // Genere
          item_category2: variant?.attributes?.typology, // Tipo prodotto
          item_category3: variant?.attributes?.season, // Stagione
          item_brand: variant?.attributes?.brand, // Marca
          item_variant: `${variant?.attributes?.colourGroupCode} / ${variant?.attributes?.size}`, // Variante
          price: variant.price?.centAmount && variant.price?.centAmount / 100, // Prezzo
          quantity: 1, // Quantità
        },
      ],
    },
  };
  sendGTMEvent(eventData);
};

export const sendSelectItemEvent = (product: Product, variant: Variant, index: number) => {
  const eventData = {
    event: 'select_item',
    ecommerce: {
      items: [
        {
          item_id: product.productId, // ID unico del prodotto
          item_name: product.name, // Nome del prodotto
          item_category: variant?.attributes?.gender, // Genere
          item_category2: variant?.attributes?.typology, // Tipo prodotto
          item_category3: variant?.attributes?.season, // Stagione
          item_brand: variant?.attributes?.brand, // Marca
          item_variant: `${variant?.attributes?.colourGroupCode} / ${variant?.attributes?.size}`, // Variante
          price: variant.price?.centAmount && variant.price?.centAmount / 100, // Prezzo
          quantity: undefined, // Quantità (se applicabile)
          index: index + 1, // Posizione nell'elenco
          list_name: 'PLP', // Nome della lista
          creative_name: undefined, // Nome creativo (opzionale)
          creative_slot: undefined, // Posizione creativa (opzionale)
        },
      ],
    },
  };
  sendGTMEvent(eventData);
};
